import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { Loader2, Merge, Search, Tags, X } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

/** Productivity → Settings → App names. Combine names that mean the same app
 *  and give anything a friendly name that shows everywhere. */
export function ProdAppNamingCard({ canManage }) {
  const [q, setQ] = usePersistedState("prod-app-naming.q", "");
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [canonical, setCanonical] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback((query) => {
    api.get(`/productivity/apps/naming`, { params: { q: query || "", limit: 300 } })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Couldn't load the application names"));
  }, []);

  useEffect(() => { load(""); }, [load]);

  const search = (e) => {
    e?.preventDefault?.();
    setPicked([]);
    load(q);
  };

  const toggle = (name) =>
    setPicked((p) => (p.includes(name) ? p.filter((n) => n !== name) : [...p, name]));

  const saveLabel = async (name, label) => {
    try {
      await api.put("/productivity/apps/label", { name, label });
      load(q);
      toast.success(label ? `Now shown as “${label}”` : "Friendly name cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that name");
      load(q);
    }
  };

  const combine = async () => {
    const target = canonical || picked[0];
    if (picked.length < 2 || !target) return;
    setBusy(true);
    try {
      await api.post("/productivity/apps/merge", {
        canonical: target, variants: picked.filter((n) => n !== target),
      });
      toast.success(`${picked.length} names now count as one app`);
      setPicked([]); setCanonical("");
      load(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't combine those names");
    } finally { setBusy(false); }
  };

  const split = async (variant) => {
    await api.post("/productivity/apps/unmerge", { names: [variant] });
    toast.success(`${variant} counts on its own again`);
    load(q);
  };

  return (
    <Card className="p-4" data-testid="prod-app-naming-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <Tags className="h-4 w-4" /> App names
      </div>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        Tick two or more rows to count them as one application, and give anything a friendly name —
        it replaces the raw name in every tab, report and email.
        {data && <> · {data.total} names · {data.named} named · {data.merged} merged</>}
      </p>

      <form onSubmit={search} className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search e.g. outlook, teams, chrome"
          className="h-8 max-w-xs" data-testid="prod-app-naming-search" />
        <Button type="submit" size="sm" variant="outline" data-testid="prod-app-naming-search-btn">
          <Search className="mr-1 h-4 w-4" /> Search
        </Button>
        {picked.length >= 2 && canManage && (
          <div className="flex items-center gap-2 rounded-md border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/40 px-2 py-1">
            <span className="text-xs text-sky-800 dark:text-sky-200">Keep as</span>
            <select value={canonical || picked[0]} onChange={(e) => setCanonical(e.target.value)}
              className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs"
              data-testid="prod-app-naming-canonical">
              {picked.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <Button size="sm" onClick={combine} disabled={busy} data-testid="prod-app-naming-combine-btn">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Merge className="mr-1 h-4 w-4" />}
              Combine {picked.length}
            </Button>
          </div>
        )}
      </form>

      {!data ? <Loader2 className="h-5 w-5 animate-spin text-slate-400 dark:text-slate-400" /> : (
        <div className="max-h-[28rem] overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/60 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-400">
              <tr>
                <th className="w-8 px-2 py-2" />
                <th className="px-2 py-2 font-semibold">Real name</th>
                <th className="px-2 py-2 font-semibold">Friendly name</th>
                <th className="px-2 py-2 text-right font-semibold">Time</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.name} className="border-t border-slate-100 dark:border-slate-800 align-top"
                  data-testid={`prod-app-naming-row-${row.name}`}>
                  <td className="px-2 py-2">
                    <Checkbox checked={picked.includes(row.name)} disabled={!canManage}
                      onCheckedChange={() => toggle(row.name)}
                      data-testid={`prod-app-naming-pick-${row.name}`} />
                  </td>
                  <td className="px-2 py-2">
                    <div className="font-mono text-xs text-slate-700 dark:text-slate-200">{row.name}</div>
                    {row.variants.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {row.variants.map((v) => (
                          <span key={v} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] text-slate-600 dark:text-slate-300">
                            {v}
                            {canManage && (
                              <button type="button" onClick={() => split(v)} title="Count separately again"
                                className="text-slate-400 dark:text-slate-400 transition-colors hover:text-rose-600"
                                data-testid={`prod-app-naming-split-${v}`}>
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <Input defaultValue={row.label} placeholder={row.name} disabled={!canManage}
                      onBlur={(e) => e.target.value.trim() !== row.label && saveLabel(row.name, e.target.value.trim())}
                      className="h-8" data-testid={`prod-app-naming-label-${row.name}`} />
                  </td>
                  <td className="px-2 py-2 text-right text-xs text-slate-500 dark:text-slate-400">{row.hms}</td>
                </tr>
              ))}
              {!data.items.length && (
                <tr><td colSpan={4} className="px-2 py-6 text-center text-xs text-slate-400 dark:text-slate-400"
                  data-testid="prod-app-naming-empty">Nothing matches that search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
